import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { HeartHandshake, LogIn, Mail, MapPin, Menu, X } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";

export const JUSTGIVING = "https://checkout.justgiving.com/n73dn87gc3";
const LINKS = [["/#about", "About"], ["/volunteer", "Volunteer"], ["/events", "Events & Talks"], ["/news", "News"], ["/#donate", "Donate"], ["/#contact", "Contact"]];

const NavItems = ({ onClick }) => LINKS.map(([to, l]) => (
  <NavLink key={to} to={to} onClick={onClick} data-testid={`public-nav-${l.toLowerCase().replace(/\W+/g, "-")}`}
    className="rounded-full px-3 py-2 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10 hover:text-white">{l}</NavLink>
));

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    const on = () => setSolid(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => {
    if (loc.hash) setTimeout(() => document.querySelector(loc.hash)?.scrollIntoView({ behavior: "smooth" }), 80);
    else window.scrollTo(0, 0);
  }, [loc.pathname, loc.hash]);
  return (
    <div className="min-h-screen overflow-x-hidden bg-[#0d0e11] text-white">
      <header data-testid="public-header" className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${solid || open ? "bg-[#0d0e11]/90 backdrop-blur-xl" : "bg-transparent"}`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3">
          <Link to="/" className="flex items-center gap-3" data-testid="public-logo">
            <img src="/brand/logo.png" alt="SSCBB logo" className="h-11 w-11" />
            <span className="hidden text-sm font-extrabold leading-tight sm:block">Shropshire · Staffordshire<br />· Cheshire Blood Bikes</span>
          </Link>
          <nav className="hidden items-center gap-1 lg:flex"><NavItems /></nav>
          <div className="flex items-center gap-2">
            <a href={JUSTGIVING} target="_blank" rel="noreferrer" data-testid="header-donate-btn" className="hidden items-center gap-1.5 rounded-full bg-[#DB0000] px-4 py-2 text-sm font-bold text-white transition-transform hover:scale-105 sm:inline-flex"><HeartHandshake className="h-4 w-4" /> Donate</a>
            <Link to="/login" data-testid="member-login-btn" className="inline-flex items-center gap-1.5 rounded-full bg-[#FEDC19] px-4 py-2 text-sm font-bold text-black transition-transform hover:scale-105"><LogIn className="h-4 w-4" /> Member login</Link>
            <button onClick={() => setOpen(!open)} className="rounded-full p-2 lg:hidden" aria-label="Menu" data-testid="public-menu-btn">{open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}</button>
          </div>
        </div>
        {open && <nav data-testid="public-mobile-nav" className="flex flex-col gap-1 px-5 pb-5 lg:hidden"><NavItems onClick={() => setOpen(false)} /></nav>}
      </header>
      <main><Outlet /></main>
      <footer id="contact" data-testid="public-footer" className="relative border-t border-white/10 bg-[#08090b]">
        <div className="h-2 chevron-stripe" />
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 md:grid-cols-4">
          <div className="md:col-span-2">
            <img src="/brand/logo.png" alt="" className="h-14 w-14" />
            <p className="mt-4 max-w-md text-sm text-white/70">An urgent volunteer service transporting blood products and samples, free of charge, to NHS hospitals across Shropshire, Staffordshire and Cheshire, 24 hours a day, 365 days a year.</p>
          </div>
          <div className="space-y-3 text-sm text-white/70">
            <h4 className="font-bold text-white">Contact</h4>
            <p className="flex gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#FEDC19]" /> SSCBB, Watling Chambers, West Suite, 214 Watling Street, Cannock, Staffordshire WS11 0BD</p>
            {[["Membership", "members@ssbbgroup.org.uk"], ["Fundraising", "fundraising@ssbbgroup.org.uk"], ["Talks", "talks@ssbbgroup.org.uk"]].map(([l, e]) => (
              <a key={e} href={`mailto:${e}`} className="flex items-center gap-2 hover:text-white"><Mail className="h-4 w-4 text-[#FEDC19]" /> {l}: {e}</a>
            ))}
          </div>
          <div className="space-y-2 text-sm text-white/70">
            <h4 className="font-bold text-white">Get involved</h4>
            <Link to="/volunteer#apply" className="block hover:text-white">Apply to volunteer</Link>
            <a href={JUSTGIVING} target="_blank" rel="noreferrer" className="block hover:text-white">Donate via JustGiving</a>
            <Link to="/events" className="block hover:text-white">Book a talk</Link>
            <Link to="/request-transport" className="block hover:text-white" data-testid="footer-request-transport">NHS: request a blood bike</Link>
            <Link to="/login" className="block hover:text-white">Member login</Link>
            <div className="pt-2"><ThemeToggle testid="public-theme-toggle" className="!text-white/70" /></div>
          </div>
        </div>
        <p className="border-t border-white/10 py-5 text-center text-xs text-white/50">© {new Date().getFullYear()} Shropshire Staffordshire Cheshire Blood Bikes · Holder of the Queen's Award for Voluntary Service</p>
      </footer>
    </div>
  );
}
